import { useState, useEffect, useRef } from "react";
import {
	getPromptPlacementStyles,
	getSetupStepDetails,
	PromptAlignment,
	PromptPosition,
	PROMPT_GAP_SPACING_PX,
	SETUP_HIGHLIGHT_STYLE,
	trackElementPosition,
	TrackerRect,
	useSetupGuideContext,
} from "./quickSetupGuideHelper";
import QuickSetupPrompt, { QuickSetupPromptProps } from "./QuickSetupPrompt";
import { CSC_ORIGIN } from "../types/common";
import { SETUP_GUIDE_ATTRIBUTES } from "./setupGuideConstants";

type TrackElementPositionFn = typeof trackElementPosition;

type TrackParams = Parameters<TrackElementPositionFn>;

type TrackOptionsParam = TrackParams[2];

type QuickSetupHighlightProps = {
	targetRef: React.RefObject<HTMLElement>;
	position?: PromptPosition;
	align?: PromptAlignment;
	trackPositionOptions?: TrackOptionsParam;
	zIndex?: number;
} & Pick<QuickSetupPromptProps, "chipText" | "title" | "description">;

export default function QuickSetupHighlight({
	targetRef,
	position = "right",
	align = "start",
	chipText,
	title,
	description,
	trackPositionOptions,
	zIndex,
}: QuickSetupHighlightProps) {
	const [coords, setCoords] = useState<TrackerRect>({ top: 0, left: 0, width: 0, height: 0 });
	const [isInViewport, setIsInViewport] = useState(false);
	const [forceRemove, setForceRemove] = useState(false);
	const [promptWidth, setPromptWidth] = useState(0);
	const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
	const promptRef = useRef<HTMLDivElement>(null);

	const setupContext = useSetupGuideContext();
	const details = title ? undefined : getSetupStepDetails(setupContext.steps, "editQuiz");
	const finalChipText = chipText ?? details?.chipText;
	const finalTitle = title ?? details?.title;
	const finalDescription = description ?? details?.description;

	const overflowsRight =
		position === "right" &&
		promptWidth > 0 &&
		coords.left + coords.width + PROMPT_GAP_SPACING_PX + promptWidth > viewportWidth;
	const overflowsLeft = position === "left" && promptWidth > 0 && coords.left - PROMPT_GAP_SPACING_PX - promptWidth < 0;
	const resolvedPosition: PromptPosition = overflowsRight || overflowsLeft ? "top" : position;

	const placementStyles = getPromptPlacementStyles(resolvedPosition, align);

	useEffect(() => {
		const promptElement = promptRef.current;
		if (!promptElement) return;

		const resizeObserver = new ResizeObserver(([entry]) => {
			if (entry) setPromptWidth(entry.contentRect.width);
		});
		resizeObserver.observe(promptElement);

		return () => resizeObserver.disconnect();
	}, []);

	useEffect(() => {
		const handleResize = () => setViewportWidth(window.innerWidth);
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	useEffect(() => {
		const currentElement = targetRef.current;
		if (!currentElement) return;
		const cleanupTracker = trackElementPosition(
			currentElement,
			(rect) => {
				setCoords(rect);

				const boundingBox = currentElement.getBoundingClientRect();

				const visible = boundingBox.top >= 0 && boundingBox.bottom <= window.innerHeight;

				setIsInViewport(visible);
			},
			trackPositionOptions,
		);

		return () => cleanupTracker();
	}, [targetRef, trackPositionOptions]);

	useEffect(() => {
		const handleSetupGuideMessage = (event: MessageEvent) => {
			try {
				const expectedOrigin = new URL(CSC_ORIGIN).origin;

				if (event.origin !== expectedOrigin) return;
			} catch (e) {
				console.error("Invalid CSC_ORIGIN URL configured:", CSC_ORIGIN);
				return;
			}

			if (event.source !== window && event.source !== window.parent) return;

			const request = event.data as {
				type: string;
			};

			if (request.type === SETUP_GUIDE_ATTRIBUTES.MESSAGE.TYPE.CLEANUP) {
				setForceRemove(true);
			}
		};

		window.addEventListener("message", handleSetupGuideMessage);

		return () => {
			window.removeEventListener("message", handleSetupGuideMessage);
		};
	}, []);

	if (forceRemove) return null;

	const shouldDisplay = coords.width > 0 && coords.height > 0 && isInViewport;

	return (
		<div
			className={SETUP_GUIDE_ATTRIBUTES.CLASSNAME.HIGHLIGHT}
			style={{
				...SETUP_HIGHLIGHT_STYLE,
				top: coords.top,
				left: coords.left,
				width: coords.width,
				height: coords.height,
				opacity: shouldDisplay ? 1 : 0,
				visibility: shouldDisplay ? "visible" : "hidden",
				transition: "opacity 0.15s ease-in-out",
				zIndex: zIndex ?? SETUP_HIGHLIGHT_STYLE.zIndex,
			}}
		>
			<div ref={promptRef} style={{ position: "absolute", ...placementStyles, pointerEvents: "auto" }}>
				<QuickSetupPrompt chipText={finalChipText} title={finalTitle} description={finalDescription} />
			</div>
		</div>
	);
}
